import React, { useState } from "react";
import { observer } from "mobx-react";
import { Search } from "lucide-react";
import { extractPublicToken } from "../helpers/constants";
import { Button, Field, Input, PageHeader, Panel } from "../../ui";

type Props = {
  /** Compact form for embedding under the swap form on the landing page. */
  compact?: boolean;
};

/**
 * Swap-token lookup. A guest's token is the only handle on their swap, so this
 * is their way back in. It resolves exactly one swap — there is no listing
 * endpoint for tokens, by design.
 */
const FindSwapBox: React.FC<Props> = observer(({ compact = false }) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const token = extractPublicToken(value);
    if (!token) {
      setError(
        "That doesn't look like a swap token. It is 32 letters and numbers — " +
          "you can paste the whole /o/… link instead."
      );
      return;
    }
    setError(null);
    // Full navigation: the server renders the swap page for this token.
    window.location.href = `/o/${token}`;
  };

  const field = (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
      <Field error={error} className="min-w-0 flex-1">
        <Input
          placeholder="Paste your swap token here"
          aria-label="Swap token"
          value={value}
          error={error}
          onChange={(e) => {
            setValue(e.currentTarget.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />
      </Field>
      <Button leftIcon={<Search size={16} />} onClick={submit}>
        Find swap
      </Button>
    </div>
  );

  if (compact) {
    return (
      <Panel className="!p-4">
        <div className="mb-2 text-sm font-semibold">Already have a swap?</div>
        <p className="mb-3 text-xs text-muted">
          Paste the swap token you saved when you created it.
        </p>
        {field}
      </Panel>
    );
  }

  return (
    <Panel>
      <PageHeader
        title="Find your swap"
        subtitle="Enter the swap token you saved."
      />
      <div className="mt-4">{field}</div>
    </Panel>
  );
});

export default FindSwapBox;
